/**
 * NumberWithUnit — InputNumber with a static unit suffix (kg, %, bales, Ne, ₹, etc.)
 *
 * Replaces AntD's deprecated InputNumber addon-suffix prop with the
 * recommended `Space.Compact` pattern, while preserving the original visual:
 * a small grey box attached to the right edge of the input.
 *
 * Why a wrapper:
 *   - Centralises one styling decision instead of 33 inline JSX blocks.
 *   - Future-proofs against another AntD change — only this file moves.
 *   - Lets AntD Form's value plumbing keep flowing (Form.Item still works).
 *
 * Usage:
 *   <NumberWithUnit unit="%" min={0} max={100} precision={2} />
 *   <Form.Item name="rate" label="Rate"><NumberWithUnit unit="kg" /></Form.Item>
 */
import { InputNumber, type InputNumberProps, Space } from 'antd'

type Props = Omit<InputNumberProps, 'addonAfter' | 'addonBefore'> & {
  unit: React.ReactNode
}

export default function NumberWithUnit({ unit, style, ...rest }: Props) {
  return (
    <Space.Compact style={{ width: '100%', display: 'flex' }}>
      <InputNumber
        {...rest}
        style={{ flex: 1, minWidth: 0, ...style }}
      />
      <span
        // Match AntD's existing addon look: subtle grey background, hairline border on the join.
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          padding: '0 11px',
          background: 'rgba(0,0,0,0.02)',
          border: '1px solid #d9d9d9',
          borderLeft: 'none',
          borderTopRightRadius: 6,
          borderBottomRightRadius: 6,
          color: 'rgba(0,0,0,0.88)',
          fontSize: 14,
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        {unit}
      </span>
    </Space.Compact>
  )
}
